/**
 * LANDING PAGE V2 — HT CAPITAL
 * --------------------------------------------------------------------
 * Inspirado em activetheory.net + theyearofgreta.com
 * Estética: Bloomberg encontra agência cinemática.
 *
 * Estrutura:
 *   - Custom cursor (crosshair de trading)
 *   - Topbar fixa minimalista
 *   - HERO fullscreen com video DNA + tipografia massiva
 *   - Marquee de tickers
 *   - Stats grid
 *   - Capítulo 001 — A MESA
 *   - Capítulo 002 — O PROCESSO (fases VECTOR/AWAKE/NOIR)
 *   - Capítulo 003 — INFRAESTRUTURA
 *   - Capítulo 004 — O OPERADOR (CTA)
 *   - Footer
 * --------------------------------------------------------------------
 */
import { useEffect, useRef, useState, useCallback, type MutableRefObject } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { AuthModal } from "@/components/landing/auth-modal";
import { useScrollAnimation, useCountUp } from "@/hooks/use-scroll-animation";
import { Download, ArrowUpRight } from "lucide-react";
import htLogoFull from "@assets/HTCAPITAL_logo-01_trimmed.png";
import heroImage from "@assets/banner-principal-1_1769378084901.png";
import { useSmoothScroll } from "@/components/landing-v2/use-smooth-scroll";
import { Parallax } from "@/components/landing-v2/parallax";
import { WorldRift } from "@/components/landing-v2/world-rift";
import "@/styles/landing-v2.css";

// HERO ESTATICO — imagem de fundo (substitui WebGL temporariamente)
// Trocar `heroImage` para usar outra imagem em attached_assets/.
function HeroStatic() {
  return (
    <div
      className="lv2-hero-static"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        backgroundImage: `url(${heroImage})`,
        backgroundColor: "#050010",
        filter: "grayscale(100%) contrast(1.05)",
      }}
    />
  );
}

// ============================================================================
// CUSTOM CURSOR — crosshair de trading com ponto laranja no centro
// ============================================================================
function CustomCursor() {
  const cursorRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
      }
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
      }
    };
    const onOver = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      setHovering(!!t.closest("a, button, [data-cursor-hover]"));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
    };
  }, []);

  return (
    <>
      <div ref={cursorRef} className={`lv2-cursor ${hovering ? "is-hover" : ""}`} />
      <div ref={dotRef} className="lv2-cursor-dot" />
    </>
  );
}

// ============================================================================
// TOPBAR — minimalista, fica transparente no topo, opaca ao scrollar
// ============================================================================
function Topbar({ onLogin, onRegister }: { onLogin: () => void; onRegister: () => void }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className={`lv2-topbar ${scrolled ? "is-scrolled" : ""}`}>
      <div className="lv2-topbar-logo">
        <img src={htLogoFull} alt="HT Capital" />
      </div>
      <nav className="lv2-topbar-nav">
        <button className="nav-link" onClick={() => scrollTo("ch-001")}>001 — Mesa</button>
        <button className="nav-link" onClick={() => scrollTo("ch-002")}>002 — Processo</button>
        <button className="nav-link" onClick={() => scrollTo("ch-003")}>003 — Infra</button>
        <button className="nav-link" onClick={() => scrollTo("ch-004")}>004 — Operador</button>
        <a href="/api/download/hts" className="cta">
          <Download size={12} strokeWidth={2.5} />
          Plataforma
        </a>
        <button onClick={onRegister} className="cta">Iniciar</button>
        <button onClick={onLogin} className="cta cta-login">Login</button>
      </nav>
    </header>
  );
}

// ============================================================================
// HERO
// Cena WebGL 3D (helice de candles) + smooth scroll progress.
// Hero fica sticky por 130vh — da peso cinematico, e a camera 3D faz
// dolly conforme o usuario rola.
// ============================================================================
function HeroSection({ onCTA }: { onCTA: () => void }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollProgress = useRef(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // calcula progresso do scroll dentro do hero (0..1)
  // alimenta CameraRig do HeroScene + faz fade do conteudo
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content) return;

    const onScroll = () => {
      const rect = wrapper.getBoundingClientRect();
      const max = wrapper.offsetHeight - window.innerHeight;
      const progress = Math.max(0, Math.min(1, -rect.top / Math.max(1, max)));
      scrollProgress.current = progress;
      const opacity = 1 - progress * 1.4;
      content.style.opacity = String(Math.max(0, opacity));
      content.style.transform = `translateY(${progress * -40}px)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const formatted = now
    .toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
    .replace(",", "");

  return (
    <div ref={wrapperRef} className="lv2-hero-scroll">
      <section className="lv2-hero">
        {/* Hero estático — imagem de fundo (subst.itui WebGL temporariamente) */}
        <HeroStatic />
        <div className="lv2-hero-overlay" />

        <div ref={contentRef} className="lv2-hero-content">
          <div className="lv2-hero-meta">
            <div>
              <span>HT Capital — Mesa Proprietária</span>
              <span>Sistema institucional · v2026.1</span>
            </div>
            <div style={{ textAlign: "right" }}>
              <span className="live">LIVE · {formatted} BRT</span>
              <span>São Paulo · Brasil</span>
            </div>
          </div>

          <h1 className="lv2-display xxl">
            Processo.
            <br />
            Capital.
            <br />
            <span className="lv2-outlined">Consistência.</span>
          </h1>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 32 }}>
            <p style={{ maxWidth: 480, color: "#bdbdbd", fontSize: 15, lineHeight: 1.6 }}>
              Mesa proprietária estruturada para avaliar, desenvolver e alocar
              operadores com base em <em style={{ color: "#fff", fontStyle: "normal" }}>dados objetivos</em>,
              controle de risco e governança operacional. Sem testes pagos.
              Sem desafios. Apenas processo.
            </p>
            <button onClick={onCTA} className="lv2-arrow-link" data-cursor-hover>
              Iniciar processo seletivo
              <ArrowUpRight size={18} />
            </button>
          </div>
        </div>

        <div className="lv2-hero-scrollhint">Role para continuar</div>
      </section>
    </div>
  );
}

// ============================================================================
// MARQUEE — fita horizontal de tickers com dados estáticos (visual)
// ============================================================================
function TickerMarquee() {
  const tickers = [
    { sym: "WIN", px: "139.450", delta: "+1.24%", up: true },
    { sym: "WDO", px: "5.087,5", delta: "-0.32%", up: false },
    { sym: "DI1F26", px: "10.78%", delta: "+12 bps", up: true },
    { sym: "DI1F27", px: "11.25%", delta: "+8 bps", up: true },
    { sym: "BVMF3", px: "11,42", delta: "+0.88%", up: true },
    { sym: "PETR4", px: "39,15", delta: "-1.20%", up: false },
    { sym: "VALE3", px: "62,08", delta: "+0.55%", up: true },
    { sym: "USDBRL", px: "5,089", delta: "-0.31%", up: false },
    { sym: "IBOV", px: "129.842", delta: "+0.92%", up: true },
    { sym: "S&P500", px: "5.847", delta: "+0.44%", up: true },
    { sym: "NDX", px: "20.193", delta: "+0.71%", up: true },
    { sym: "BRENT", px: "82,45", delta: "+1.05%", up: true },
  ];
  // duplica para rolagem infinita sem corte
  const items = [...tickers, ...tickers];
  return (
    <div className="lv2-marquee" aria-hidden>
      <div className="lv2-marquee-track">
        {items.map((t, i) => (
          <div key={i} className="lv2-ticker">
            <span className="sym">{t.sym}</span>
            <span className="px">{t.px}</span>
            <span className={`delta ${t.up ? "up" : "down"}`}>{t.delta}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// REVEAL WRAPPER — fade-up on scroll
// ============================================================================
function Reveal({
  children,
  className = "",
  stagger = false,
  threshold = 0.15,
}: {
  children: React.ReactNode;
  className?: string;
  stagger?: boolean;
  threshold?: number;
}) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>({ threshold });
  return (
    <div
      ref={ref}
      className={`${stagger ? "lv2-reveal-stagger" : "lv2-reveal"} ${isVisible ? "is-visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

// ============================================================================
// STATS GRID
// ============================================================================
function StatsGrid({
  stats,
}: {
  stats: { operadores: number; pregoes: number; ori: number; aprovacao: number };
}) {
  return (
    <div className="lv2-stats">
      <Stat value={stats.operadores} suffix="+" label="Operadores ativos" />
      <Stat value={stats.pregoes} label="Pregões processados" />
      <Stat value={stats.ori} suffix="%" label="ORI médio" />
      <Stat value={stats.aprovacao} suffix="%" label="Taxa de aprovação" />
    </div>
  );
}
function Stat({ value, label, suffix = "" }: { value: number; label: string; suffix?: string }) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.4 });
  const { count, startAnimation } = useCountUp(value, 1800);
  useEffect(() => {
    if (isVisible) startAnimation();
  }, [isVisible, startAnimation]);
  return (
    <div ref={ref} className="lv2-stat">
      <div className="lv2-stat-value">
        {count.toLocaleString("pt-BR")}
        {suffix}
      </div>
      <div className="lv2-stat-label">{label}</div>
    </div>
  );
}

// ============================================================================
// CHAPTER — wrapper genérico com número 001/002/etc
// ============================================================================
function Chapter({
  id,
  number,
  eyebrow,
  title,
  children,
}: {
  id: string;
  number: string;
  eyebrow: string;
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="lv2-chapter">
      <Parallax speed={0.45} className="lv2-chapter-bgnum">{number}</Parallax>
      <div className="lv2-chapter-num">CHAPTER {number}</div>
      <Reveal>
        <Parallax speed={-0.08}>
          <div className="lv2-chapter-eyebrow">{eyebrow}</div>
        </Parallax>
        <Parallax speed={-0.18}>
          <h2 className="lv2-display xl lv2-chapter-title">{title}</h2>
        </Parallax>
      </Reveal>
      <Reveal stagger threshold={0.1}>
        {children}
      </Reveal>
    </section>
  );
}

// ============================================================================
// FAQ — categorias + accordion brutalista
// ============================================================================
const FAQ_CATEGORIES = [
  {
    id: "sobre",
    name: "Sobre a Mesa",
    questions: [
      {
        q: "O que é a HT CAPITAL?",
        a: "A HT CAPITAL é uma mesa proprietária institucional, estruturada para avaliar, desenvolver e alocar operadores com base em dados objetivos, controle de risco e governança operacional. Não somos uma plataforma de desafios pagos, nem um produto educacional. Nosso foco é processo, consistência e longevidade operacional.",
      },
      {
        q: "Existe teste pago ou desafio pago?",
        a: "Não. A HT CAPITAL não trabalha com testes pagos, desafios comerciais ou venda de avaliações. O processo seletivo é feito por entrevista técnica e comportamental, seguida de avaliação do operador em conta real, sob mandato definido. Não cobramos para avaliar operadores.",
      },
      {
        q: "Preciso depositar capital para participar do processo seletivo?",
        a: "Sim. A avaliação ocorre em conta real, com capital próprio do operador. Esse capital permanece em uma conta de corretagem em nome do próprio operador — a HT CAPITAL não tem custódia, não opera o capital, não recebe valores a título de inscrição, taxa ou desafio. O depósito é exclusivamente para viabilizar a operação real, sob mandato e métrica. Em qualquer momento o operador pode sacar e encerrar a participação.",
      },
    ],
  },
  {
    id: "processo",
    name: "Processo Seletivo",
    questions: [
      {
        q: "Como funciona o processo seletivo?",
        a: "O processo ocorre em fases: (1) Entrevista inicial — avaliamos perfil, histórico, expectativas e aderência ao modelo da mesa; (2) Fase VECTOR — o operador atua em conta real, com capital próprio, sob mandato restrito, focando em disciplina e controle de risco; (3) Progressão automática — a evolução ocorre exclusivamente por métricas, sem decisão subjetiva.",
      },
      {
        q: "O que é a fase VECTOR?",
        a: "VECTOR é a fase inicial do processo seletivo operacional. Nessa etapa, o operador opera em ambiente real, segue limites rigorosos e é avaliado por métricas comportamentais objetivas. Resultados pontuais não são critério de aprovação. O objetivo é validar processo e consistência.",
      },
    ],
  },
  {
    id: "fases",
    name: "Fases Operacionais",
    questions: [
      {
        q: "O que acontece ao passar para a fase AWAKE?",
        a: "A fase AWAKE representa a aprovação no processo seletivo. O operador demonstrou capacidade de sustentar processo em conta real, passa a operar sob mandato expandido, recebe maior autonomia operacional e tem acesso a treinamento técnico exclusivo. É uma fase de consolidação, não de risco elevado.",
      },
      {
        q: "O que é a fase NOIR?",
        a: "NOIR é a fase sênior da HT CAPITAL, reservada a operadores com histórico comprovado de excelência técnica, consistência e gestão de risco impecável. O operador atua com mandato premium, recebe buying power inicial de R$ 100.000, integra um regime profissional diferenciado e conta com mínimo garantido mensal de R$ 4.000. NOIR não é prêmio, é responsabilidade institucional.",
      },
      {
        q: "O mínimo garantido significa salário fixo?",
        a: "Não. O mínimo garantido é um mecanismo contratual de estabilidade operacional, aplicado exclusivamente a operadores NOIR, para permitir foco em processo e execução, mesmo em meses adversos. Não caracteriza vínculo empregatício, promessa de renda ou garantia de resultado.",
      },
      {
        q: "Como funciona a divisão de resultados?",
        a: "Durante a fase VECTOR, que corresponde ao período de avaliação em conta real, 100% do resultado líquido é do operador. Não há divisão de lucros, taxas de performance ou retenções sobre o resultado operacional nessa fase. À medida que o operador evolui para fases superiores, o modelo de alocação de capital e remuneração passa a seguir regras específicas da fase, sempre formalizadas em mandato e disponíveis no dashboard.",
      },
    ],
  },
  {
    id: "operacional",
    name: "Operacional",
    questions: [
      {
        q: "Qual é a plataforma operacional?",
        a: "A plataforma oficial do operador é o HTS — HT Trading Station, desenvolvida internamente pela HT CAPITAL para Windows e macOS. Não utilizamos plataformas de terceiros para execução. No HTS o operador encontra gráfico, SuperDOM, book, ferramentas quantitativas para leitura de juros, risk monitor, mandato vivo e auditoria contínua, tudo integrado ao nosso Order Management System.",
      },
      {
        q: "Quais ativos posso operar?",
        a: "O operador tem acesso a multimercado: B3 (mini índice, mini dólar, índice cheio, dólar cheio, ações, milho, boi, café e demais futuros listados), FX global, ouro (XAU/USD), petróleo (WTI/Brent), índices globais (S&P, Nasdaq, Dow, DAX, Nikkei) e criptomoedas. A liberação de cada classe de ativo segue o mandato vigente da fase operacional.",
      },
      {
        q: "As ordens são em ambiente simulado?",
        a: "Não. A HT CAPITAL não opera com simulador em nenhuma fase. Toda ordem é executada em conta real, em corretora regulada, e roteada pelo HTS para a corretora de menor latência. Não existe paper trading, conta demo ou execução virtual em momento algum do processo.",
      },
      {
        q: "Qual é o valor mínimo de depósito?",
        a: "Não existe valor mínimo padrão. O depósito é dimensionado conforme o gerenciamento operacional do próprio operador — quanto ele opera por contrato, quantos contratos pretende operar simultaneamente e qual a margem requerida pela B3 para os ativos do mandato. O capital fica em conta de corretagem em nome da HT CAPITAL, sob custódia da corretora, e pode ser sacado a qualquer momento.",
      },
      {
        q: "Existe treinamento ou suporte técnico?",
        a: "Sim. A HT CAPITAL mantém uma trilha técnica estruturada, liberada conforme a fase operacional do operador. O conteúdo é voltado a leitura institucional de mercado, estrutura de risco, execução profissional e governança operacional. O treinamento é 100% gratuito para os operadores da mesa — a HT CAPITAL não vende cursos, não comercializa conteúdo educacional e não monetiza formação. Não se trata de curso genérico, mas de formação aplicada ao modelo da mesa.",
      },
      {
        q: "Como são calculados os resultados?",
        a: "Todos os resultados são apurados em regime End of Day (EOD), com base nos dados oficiais de execução do nosso Order Management System. O extrato da plataforma é a fonte oficial e soberana de apuração, incluindo resultado operacional, custos de bolsa, corretagem, taxas de plataforma e ajustes quando aplicáveis.",
      },
      {
        q: "As regras podem mudar?",
        a: "Sim. A HT CAPITAL pode ajustar limites, parâmetros de risco, critérios de avaliação e taxas operacionais sempre que necessário para proteção sistêmica. Alterações são versionadas, registradas e comunicadas oficialmente.",
      },
      {
        q: "Posso operar fora do mandato?",
        a: "Não. Operar fora do mandato constitui violação grave e pode resultar em bloqueio automático, regressão de fase ou encerramento da participação.",
      },
    ],
  },
  {
    id: "avisos",
    name: "Avisos e Perfil",
    questions: [
      {
        q: "Existe garantia de lucro?",
        a: "Não. Operar mercados financeiros envolve riscos elevados, incluindo a possibilidade de perda total de capital. A HT CAPITAL não oferece promessa de retorno, garantia de resultado ou recomendação de investimento.",
      },
      {
        q: "Qual o perfil de operador que a HT CAPITAL busca?",
        a: "Buscamos operadores que respeitam processo, priorizam controle de risco, aceitam governança e auditoria, e entendem que consistência importa mais que performance pontual. A estrutura não é adequada para quem busca atalhos, apostas ou soluções rápidas.",
      },
      {
        q: "Como posso iniciar o processo?",
        a: "O processo se inicia com o cadastro na plataforma da HT CAPITAL, que concede acesso imediato ao ambiente oficial do operador. A equipe da HT CAPITAL analisa o perfil cadastrado e entra em contato para a etapa de entrevista, quando aplicável. Não existem testes pagos, desafios automatizados ou processos externos à plataforma. O cadastro não implica aprovação automática — a entrada como operador depende de análise, entrevista e aderência ao modelo operacional da mesa.",
      },
    ],
  },
];

function FAQSection() {
  const [activeCat, setActiveCat] = useState(FAQ_CATEGORIES[0].id);
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const current = FAQ_CATEGORIES.find((c) => c.id === activeCat) ?? FAQ_CATEGORIES[0];

  return (
    <Chapter
      id="ch-004"
      number="004"
      eyebrow="Dúvidas Frequentes"
      title={<>Sem promessas.<br />Só perguntas reais.</>}
    >
      <div className="lv2-chapter-body" style={{ display: "block" }}>
        <div className="lv2-faq-tabs">
          {FAQ_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              data-cursor-hover
              className={`lv2-faq-tab ${cat.id === activeCat ? "is-active" : ""}`}
              onClick={() => {
                setActiveCat(cat.id);
                setOpenIdx(0);
              }}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="lv2-faq-list">
          {current.questions.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={item.q} className={`lv2-faq-item ${open ? "is-open" : ""}`}>
                <button
                  type="button"
                  className="lv2-faq-q"
                  data-cursor-hover
                  onClick={() => setOpenIdx(open ? null : i)}
                >
                  <span style={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <span className="lv2-faq-q-num">{String(i + 1).padStart(2, "0")}</span>
                    <span>{item.q}</span>
                  </span>
                  <span className="lv2-faq-q-icon" aria-hidden="true" />
                </button>
                <div className="lv2-faq-a">
                  <p style={{ margin: 0 }}>{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Chapter>
  );
}

// ============================================================================
// FOOTER
// ============================================================================
function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="lv2-footer">
      <div className="lv2-footer-grid">
        <div className="lv2-footer-col">
          <img src={htLogoFull} alt="HT Capital" style={{ height: 24, marginBottom: 24 }} />
          <p style={{ color: "#888", fontSize: 14, lineHeight: 1.6, maxWidth: 380 }}>
            Mesa proprietária institucional. Operamos sob princípios de risco,
            governança e processo. Sem promessas de retorno. Sem atalhos.
          </p>
        </div>
        <div className="lv2-footer-col">
          <h4>Mesa</h4>
          <ul>
            <li><a href="#ch-001">A Mesa</a></li>
            <li><a href="#ch-002">Processo</a></li>
            <li><a href="#ch-003">Infraestrutura</a></li>
            <li><a href="#ch-004">FAQ</a></li>
            <li><a href="#ch-005">Operador</a></li>
          </ul>
        </div>
        <div className="lv2-footer-col">
          <h4>Plataforma</h4>
          <ul>
            <li><a href="/api/download/hts">Baixar HTS</a></li>
            <li><a href="/login">Login</a></li>
            <li><a href="/processo-seletivo">Processo Seletivo</a></li>
          </ul>
        </div>
        <div className="lv2-footer-col">
          <h4>Contato</h4>
          <ul>
            <li><a href="mailto:contato@hightrading.com.br">contato@hightrading.com.br</a></li>
          </ul>
        </div>
      </div>
      <div className="lv2-footer-bottom">
        <span>© {year} HT Capital · São Paulo, BR</span>
        <span>v2026.1 · Mesa Proprietária Institucional</span>
      </div>
    </footer>
  );
}

// ============================================================================
// PAGE
// ============================================================================
export default function LandingPage() {
  const [, navigate] = useLocation();
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "register">("login");

  // ativa smooth scroll global (Lenis) — sensação Active Theory
  useSmoothScroll();

  const { data: siteSettings } = useQuery<Record<string, string>>({
    queryKey: ["/api/site-settings/public"],
    staleTime: 1000 * 60 * 5,
  });

  const stats = {
    operadores: parseInt(siteSettings?.operadores_ativos || "47"),
    pregoes: parseInt(siteSettings?.pregoes_processados || "12847"),
    ori: parseInt(siteSettings?.ori_medio || "72"),
    aprovacao: parseInt(siteSettings?.taxa_aprovacao || "94"),
  };

  const openLogin = useCallback(() => {
    setAuthTab("login");
    setAuthOpen(true);
  }, []);
  const openRegister = useCallback(() => {
    navigate("/processo-seletivo");
  }, [navigate]);

  return (
    <div className="lv2">
      <CustomCursor />
      <Topbar onLogin={openLogin} onRegister={openRegister} />

      <HeroSection onCTA={openRegister} />

      {/* ============================================================ */}
      {/* CAPÍTULO 001 — A MESA  (logo após hero, segue o flash branco) */}
      {/* ============================================================ */}
      <Chapter
        id="ch-001"
        number="001"
        eyebrow="Sobre a Mesa"
        title={<>Não somos<br />curso. Não somos<br /><span className="lv2-outlined">desafio.</span></>}
      >
        <div className="lv2-chapter-body">
          <div>
            <p>
              A HT CAPITAL é uma mesa proprietária institucional. Estruturada
              para avaliar, desenvolver e alocar operadores em conta real, sob
              mandato definido. O processo é técnico, auditável e versionado.
            </p>
            <p style={{ marginTop: 24 }}>
              Não vendemos avaliações. Não cobramos para testar operadores.
              A entrada na mesa depende exclusivamente de aderência ao modelo
              operacional, controle de risco e consistência demonstrada em
              ambiente real.
            </p>
          </div>
          <div>
            <div className="lv2-divider" style={{ marginBottom: 32 }}>
              <span>Princípios</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 20 }}>
              {[
                ["01", "Processo acima de performance pontual"],
                ["02", "Risco sistêmico antes de retorno individual"],
                ["03", "Decisão por métrica, não por opinião"],
                ["04", "Governança e auditabilidade total"],
                ["05", "Avaliação em conta real"],
              ].map(([n, t]) => (
                <li key={n} style={{ display: "flex", gap: 24, alignItems: "baseline", borderBottom: "1px solid var(--line)", paddingBottom: 16 }}>
                  <span className="lv2-mono" style={{ color: "var(--lv2-accent)", fontSize: 12 }}>{n}</span>
                  <span style={{ fontSize: 17, color: "#ddd" }}>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Chapter>

      <TickerMarquee />

      <StatsGrid stats={stats} />

      {/* ============================================================ */}
      {/* CAPÍTULO 002 — O PROCESSO                                     */}
      {/* ============================================================ */}
      <Chapter
        id="ch-002"
        number="002"
        eyebrow="Modelo Operacional"
        title={<>Três fases.<br />Uma trajetória.</>}
      >
        <div style={{ gridColumn: "1 / -1" }}>
          <p style={{ maxWidth: 720, fontSize: 18, color: "#b8b8b8", lineHeight: 1.6 }}>
            O operador percorre uma trilha de progressão estritamente baseada em
            métricas. Sem decisão subjetiva. Sem aceleração comercial. Cada
            fase tem mandato, limites e regras formalizadas.
          </p>
          <div className="lv2-phases">
            <div className="lv2-phase" data-cursor-hover>
              <div className="num">FASE 01</div>
              <div className="name">Vector</div>
              <div className="desc">
                Avaliação em conta real, capital próprio, mandato restrito.
                Foco em disciplina e controle de risco. 100% do resultado
                líquido permanece com o operador.
              </div>
              <div className="meta">
                <span>Avaliação</span>
                <span>—</span>
              </div>
            </div>
            <div className="lv2-phase" data-cursor-hover>
              <div className="num">FASE 02</div>
              <div className="name">Awake</div>
              <div className="desc">
                Aprovação confirmada. Mandato expandido, autonomia operacional,
                trilha técnica exclusiva. Fase de consolidação, não de risco
                elevado.
              </div>
              <div className="meta">
                <span>Aprovação</span>
                <span>—</span>
              </div>
            </div>
            <div className="lv2-phase" data-cursor-hover>
              <div className="num">FASE 03</div>
              <div className="name">Noir</div>
              <div className="desc">
                Operação sênior. Mandato premium, buying power inicial de
                R$ 100.000, mínimo garantido mensal de R$ 4.000. Responsabilidade
                institucional, não prêmio.
              </div>
              <div className="meta">
                <span>Sênior</span>
                <span>R$ 100.000</span>
              </div>
            </div>
          </div>
        </div>
      </Chapter>

      {/* ============================================================ */}
      {/* CAPÍTULO 003 — INFRAESTRUTURA                                 */}
      {/* ============================================================ */}
      <Chapter
        id="ch-003"
        number="003"
        eyebrow="Infraestrutura Técnica"
        title={<>Stack institucional.<br />Engine proprietária.</>}
      >
        <div className="lv2-chapter-body">
          <div>
            <p>
              Operamos com Risk Management System próprio, ledger contábil
              auditável e processamento de eventos em tempo real. Toda
              ordem é registrada, toda execução é apurada em regime End of
              Day (EOD), com base nos dados oficiais do nosso Order
              Management System.
            </p>
            <p style={{ marginTop: 24 }}>
              A plataforma HTS — instalável em Windows e macOS — entrega
              ao operador um ambiente único multimercado, ordens, risk
              monitor, mandato vivo e auditoria contínua. Sem mistério.
              Tudo visível. Tudo versionado.
            </p>
            <a href="/api/download/hts" className="lv2-arrow-link" style={{ marginTop: 32, display: "inline-flex" }} data-cursor-hover>
              <Download size={16} />
              Baixar HTS Trading Station
            </a>
          </div>
          <div>
            <div className="lv2-divider" style={{ marginBottom: 32 }}>
              <span>Stack</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1px", background: "var(--line)" }}>
              {[
                ["Engine", "Risk + OMS proprietários"],
                ["Latência", "Match-first architecture"],
                ["Apuração", "EOD via Profit oficial"],
                ["Ledger", "Contábil double-entry"],
                ["Realtime", "Multimercado (Globais + B3)"],
                ["Auditoria", "Versioned + assinada"],
              ].map(([k, v]) => (
                <div key={k} style={{ background: "var(--bg)", padding: "20px 24px" }}>
                  <div className="lv2-mono" style={{ fontSize: 11, color: "var(--muted)", letterSpacing: "0.2em", textTransform: "uppercase" }}>{k}</div>
                  <div style={{ marginTop: 8, fontSize: 14, color: "#ddd" }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Chapter>

      {/* ============================================================ */}
      {/* CAPÍTULO 004 — FAQ                                            */}
      {/* ============================================================ */}
      <FAQSection />

      {/* ============================================================ */}
      {/* CAPÍTULO 005 — O OPERADOR (CTA)                               */}
      {/* ============================================================ */}
      <section id="ch-005" className="lv2-cta">
        <div className="lv2-cta-bg" />
        <Reveal>
          <div className="lv2-chapter-eyebrow" style={{ marginBottom: 32 }}>005 — O Operador</div>
          <h2 className="lv2-display xl">
            Se você opera por
            <br />
            <span className="lv2-outlined">processo</span>, a mesa está aberta.
          </h2>
          <p style={{ maxWidth: 600, margin: "32px auto 48px", color: "#b8b8b8", fontSize: 17, lineHeight: 1.6 }}>
            O cadastro abre acesso imediato ao ambiente oficial do operador.
            A entrevista técnica e a avaliação ocorrem dentro da plataforma.
            Não há atalhos. Não há promessas. Apenas um modelo, um mandato e
            uma trajetória.
          </p>
          <button onClick={openRegister} className="lv2-cta-btn" data-cursor-hover>
            Iniciar processo seletivo
            <ArrowUpRight size={18} />
          </button>
        </Reveal>
      </section>

      <Footer />

      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        defaultTab={authTab}
      />
    </div>
  );
}
